import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleMap, useJsApiLoader, Marker, Circle } from "@react-google-maps/api";
import { MapPin, Plus, CheckCircle2, ArrowRight, X, Loader2, Navigation, Home, Briefcase, Globe } from "lucide-react";
import { api } from "../services/api";

const PRIMARY = "#363C90";
const MAP_API_KEY = "AIzaSyBz3baoM59as3Ww1mtx-dFwV2nqJ3eA6_A"; 

const containerStyle = { width: '100%', height: '300px', borderRadius: '20px' };

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const Address: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = location.state || {};

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: MAP_API_KEY,
    libraries: ['places']
  });

  const [addresses, setAddresses] = useState<any[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState<any>(null);

  const [markerCoord, setMarkerCoord] = useState<any>(null);
  const [formData, setFormData] = useState({ label: "Home", address: "", zip: "" });
  const [saving, setSaving] = useState(false);

  // --- CORE LOGIC (UNTOUCHED) ---
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const cityData = localStorage.getItem("selected_city_full_data");
      if (cityData) setSelectedCity(JSON.parse(cityData));
      const token = localStorage.getItem("user_token");
      const res = await (api.get as any)("/addresses", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || res;
      setAddresses(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    loadInitialData(); 
    const handleCitySync = () => {
      loadInitialData();
      setSelected(null);
    };
    window.addEventListener("cityUpdated", handleCitySync);
    return () => window.removeEventListener("cityUpdated", handleCitySync);
  }, [loadInitialData]);

  const updateAddressFromCoords = (lat: number, lng: number) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const fullAddr = results[0].formatted_address;
        const postCode = results[0].address_components.find(c => c.types.includes("postal_code"))?.long_name || "";
        setFormData(prev => ({ ...prev, address: fullAddr, zip: postCode }));
      }
    });
  };

  const handleMapAction = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMarkerCoord({ latitude: lat, longitude: lng });
      updateAddressFromCoords(lat, lng);
    }
  };

  const saveAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!markerCoord || !selectedCity) return;
    const dist = calculateDistance(
      markerCoord.latitude, markerCoord.longitude,
      parseFloat(selectedCity.lat || "0"),
      parseFloat(selectedCity.lng || "0")
    );
    if (dist > parseFloat(selectedCity.radius_km || "0")) {
      alert(`Out of Service Area: This location is outside our range.`);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("user_token");
      await (api.post as any)("/addresses", {
        ...formData,
        city: selectedCity.name,
        lat: markerCoord.latitude.toString(),
        lng: markerCoord.longitude.toString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      setFormData({ label: "Home", address: "", zip: "" });
      loadInitialData();
    } catch (e) {
      alert("Error saving address");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="h-[80vh] flex flex-col items-center justify-center bg-[#F8FAFC]"><Loader2 className="animate-spin text-[#363C90] mb-4" size={32} /><p className="text-[10px] font-black uppercase tracking-[3px] text-slate-400">Loading your addresses...</p></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      
      {/* --- SLIM WEB HEADER --- */}
      <div className="bg-white border-b border-slate-100 py-6 px-6">
        <div className="max-w-[1000px] mx-auto flex items-center justify-between">
           <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight leading-none">Pickup Address</h1>
              <p className="text-slate-400 text-xs font-bold mt-2 uppercase tracking-wide flex items-center gap-1.5">
                <Navigation size={12} className="text-[#363C90]" /> 
                Serving in <span className="text-[#363C90]">{selectedCity?.name || "Jaipur"}</span>
              </p>
           </div>
           <button 
             onClick={() => navigate(-1)}
             className="hidden md:flex items-center gap-2 text-slate-400 hover:text-slate-600 text-[10px] font-black uppercase tracking-widest"
           >
             Go Back
           </button>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT: ADDRESS LIST */}
          <div className="lg:col-span-7 space-y-4">
             <div className="flex items-center justify-between px-2">
                <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Saved Locations</h3>
                <span className="text-[10px] font-bold text-slate-300 italic">{addresses.length} Addresses Found</span>
             </div>

             <div className="space-y-3">
                {addresses.filter(a => a.city?.toLowerCase() === (selectedCity?.name || "").toLowerCase()).map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelected(item.id)}
                    className={`group p-5 rounded-2xl cursor-pointer border transition-all duration-200 relative overflow-hidden
                      ${selected === item.id 
                        ? "bg-white border-[#363C90] shadow-lg ring-1 ring-[#363C90]" 
                        : "bg-white border-slate-100 hover:border-slate-200 shadow-sm"}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                         <div className={`p-1.5 rounded-lg ${selected === item.id ? "bg-indigo-50 text-[#363C90]" : "bg-slate-50 text-slate-400"}`}>
                            {item.label === 'Home' ? <Home size={14} /> : item.label === 'Office' ? <Briefcase size={14} /> : <Globe size={14} />}
                         </div>
                         <span className={`text-[10px] font-black uppercase tracking-widest ${selected === item.id ? "text-[#363C90]" : "text-slate-400"}`}>
                           {item.label}
                         </span>
                      </div>
                      {selected === item.id && <CheckCircle2 className="text-[#363C90]" size={18} />}
                    </div>
                    <p className={`text-[13px] font-bold leading-relaxed pr-8 ${selected === item.id ? "text-slate-900" : "text-slate-600"}`}>
                      {item.address}
                    </p>
                  </div>
                ))}

                <button
                  onClick={() => {
                    const lat = parseFloat(selectedCity?.lat || "26.9124");
                    const lng = parseFloat(selectedCity?.lng || "75.7873");
                    setMarkerCoord({ latitude: lat, longitude: lng });
                    updateAddressFromCoords(lat, lng);
                    setShowModal(true);
                  }}
                  className="w-full py-6 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 font-black text-[11px] uppercase tracking-[2px] flex items-center justify-center gap-2 hover:bg-white hover:border-[#363C90] hover:text-[#363C90] transition-all group"
                >
                  <Plus size={18} className="group-hover:rotate-90 transition-transform" /> Add New Pickup Point
                </button>
             </div>
          </div>

          {/* RIGHT: SIDEBAR ACTION */}
          <div className="lg:col-span-5 lg:sticky lg:top-8">
            <div className="bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm">
               <h3 className="text-sm font-black text-slate-900 mb-6 uppercase tracking-tight">Final Step</h3>
               
               <div className="bg-slate-50 rounded-2xl p-4 mb-8">
                  <p className="text-[11px] text-slate-500 font-bold leading-relaxed mb-4 uppercase tracking-wide">Selected Address Info:</p>
                  {selected ? (
                    <div className="flex gap-3 items-start">
                       <MapPin className="text-[#363C90] shrink-0" size={16} />
                       <p className="text-[12px] font-bold text-slate-800 italic leading-snug">
                         {addresses.find(a => a.id === selected)?.address}
                       </p>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 font-medium italic italic">Please select or add an address to continue...</p>
                  )}
               </div>

               <button
                 disabled={!selected}
                 onClick={() => navigate("/sell/slot", { state: { ...params, addressId: selected } })}
                 className="w-full py-5 bg-[#363C90] text-white rounded-2xl font-black text-xs flex items-center justify-center gap-3 disabled:opacity-30 disabled:grayscale shadow-xl shadow-indigo-900/10 hover:translate-y-[-2px] transition-all uppercase tracking-[2px]"
               >
                 PICK A TIME SLOT <ArrowRight size={18} />
               </button>
            </div>
          </div>

        </div>
      </div>

      {/* --- ADD ADDRESS MODAL --- */}
      {showModal && isLoaded && (
        <div className="fixed inset-0 z-[110] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
          <div className="bg-white w-full max-w-[500px] rounded-[32px] overflow-hidden flex flex-col max-h-[90vh] shadow-2xl">
            <div className="p-6 flex justify-between items-center border-b border-slate-50">
              <h2 className="text-lg font-black tracking-tight">Mark Pickup Location</h2>
              <button onClick={() => setShowModal(false)} className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="relative">
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={{ lat: markerCoord?.latitude || 26.9124, lng: markerCoord?.longitude || 75.7873 }}
                  zoom={15}
                  onClick={handleMapAction}
                  options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy' }}
                >
                  {markerCoord && (
                    <Marker
                      position={{ lat: markerCoord.latitude, lng: markerCoord.longitude }}
                      draggable
                      onDragEnd={(e) => handleMapAction(e as any)}
                    />
                  )}
                  {selectedCity?.lat && (
                    <Circle
                      center={{ lat: parseFloat(selectedCity.lat), lng: parseFloat(selectedCity.lng) }}
                      radius={parseFloat(selectedCity.radius_km || "0") * 1000}
                      options={{ fillColor: PRIMARY, fillOpacity: 0.1, strokeColor: PRIMARY, strokeWeight: 1, clickable: false }}
                    />
                  )}
                </GoogleMap>
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg border border-slate-100 shadow-sm text-[9px] font-black uppercase text-[#363C90] tracking-widest">
                   Move pin to exact location
                </div>
              </div>

              <form onSubmit={saveAddress} className="p-6 space-y-4">
                <div className="flex gap-2">
                  {['Home', 'Office', 'Other'].map(tag => (
                    <button
                      key={tag} type="button"
                      onClick={() => setFormData({ ...formData, label: tag })}
                      className={`flex-1 py-3 rounded-xl text-[10px] font-black border-2 transition-all uppercase tracking-widest ${formData.label === tag ? "border-[#363C90] bg-indigo-50 text-[#363C90]" : "border-slate-50 text-slate-300"}`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                
                <textarea
                  placeholder="Street details, House No, Building..."
                  className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-[#363C90] focus:bg-white transition-all outline-none font-bold text-xs h-20 resize-none"
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  required
                />
                
                <div className="flex gap-3">
                  <div className="flex-1 p-4 bg-slate-100 rounded-2xl font-black text-[11px] text-slate-400 uppercase tracking-widest truncate">
                    {selectedCity?.name || "Jaipur"}
                  </div>
                  <input
                    placeholder="ZIP"
                    maxLength={6}
                    className="w-1/3 p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-[#363C90] focus:bg-white transition-all outline-none font-black text-xs"
                    value={formData.zip}
                    onChange={e => setFormData({ ...formData, zip: e.target.value.replace(/\D/g, '') })}
                    required
                  />
                </div>

                <button type="submit" disabled={saving} className="w-full py-4 bg-[#363C90] text-white rounded-2xl font-black text-[11px] uppercase tracking-[2px] flex items-center justify-center gap-2 shadow-lg shadow-indigo-900/20 active:scale-95 transition-all">
                  {saving ? <Loader2 className="animate-spin" size={18} /> : "Save & Use Address"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Address;